import React, { useState } from "react";
import { MaskedSection } from "@semo/shared-ui";
import { formatPhoneNumber } from "@semo/shared-utils";

interface QuoteProviderInfoProps {
  provider?: {
    registrationNumber: string;
    companyName: string;
    representative: string;
    addressFull: string;
    businessType: string;
    item?: string;
    managerName: string;
    managerPhone: string;
    companySealUrl?: string;
  };
  isBlur?: boolean;
}

export function QuoteProviderInfo({
  provider,
  isBlur = false,
}: QuoteProviderInfoProps) {
  // 직인 주소가 열리지 않으면(예: 만료된 외부 주소) 깨진 그림 대신 «(인)» 자리로 돌린다(V-15).
  const [failedSealUrl, setFailedSealUrl] = useState<string | null>(null);
  const sealUrl =
    provider?.companySealUrl && provider.companySealUrl !== failedSealUrl
      ? provider.companySealUrl
      : null;

  return (
    <MaskedSection
      isMasked={isBlur}
      scale="document"
      barCount={4}
      message={
        <>
          공급사 정보는
          <br />
          계약 후에 확인할 수 있어요
        </>
      }
    >
      <div className="mb-6 sm:mb-8 border border-[#dbe8f7] rounded-lg overflow-x-auto">
        <table className="w-full text-2xs sm:text-sm md:text-sm min-w-[500px]">
          <tbody>
            <tr>
              <td
                rowSpan={4}
                className="bg-[#e9f4ff] w-8 sm:w-10 md:w-12 text-center font-semibold text-primary-text border-r border-r-[#dbe8f7] border-b border-b-[#eef2f7] p-0 align-middle"
              >
                <div className="flex flex-col items-center justify-center h-full py-2 sm:py-3 md:py-4 leading-6 sm:leading-7 md:leading-8">
                  <span>공</span>
                  <span>급</span>
                  <span>자</span>
                </div>
              </td>
              <td className="bg-[#fafcff] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-b border-[#eef2f7]">
                등록번호
              </td>
              <td
                colSpan={3}
                className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 font-medium border-b border-[#eef2f7]"
              >
                {provider?.registrationNumber || "알 수 없음"}
              </td>
            </tr>
            <tr>
              <td className="bg-[#fafcff] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-b border-[#eef2f7]">
                상호(법인명)
              </td>
              <td className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 font-medium border-r border-b border-[#eef2f7]">
                {provider?.companyName || "알 수 없음"}
              </td>
              <td className="bg-[#fafcff] w-[60px] sm:w-[70px] md:w-[80px] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-b border-[#eef2f7]">
                성명
              </td>
              <td className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 font-medium border-b border-[#eef2f7]">
                <div className="flex items-center justify-between gap-2">
                  <span>{provider?.representative || "알 수 없음"}</span>
                  <div
                    className={`shrink-0 w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-2xs sm:text-2xs text-gray-300 select-none overflow-hidden bg-[#f9f9f9] ${
                      sealUrl ? "" : "border-2 border-dashed border-gray-300"
                    }`}
                  >
                    {sealUrl ? (
                      <img
                        src={sealUrl}
                        alt="도장"
                        className="w-full h-full object-contain"
                        onError={() => setFailedSealUrl(sealUrl)}
                      />
                    ) : (
                      <span>(인)</span>
                    )}
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td className="bg-[#fafcff] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-b border-[#eef2f7]">
                사업장 주소
              </td>
              <td
                colSpan={3}
                className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 border-b border-[#eef2f7] text-2xs sm:text-xs md:text-sm"
              >
                {provider?.addressFull || "알 수 없음"}
              </td>
            </tr>
            <tr>
              <td className="bg-[#fafcff] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-[#eef2f7]">
                업태
              </td>
              <td className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 border-r border-[#eef2f7] text-2xs sm:text-xs md:text-sm">
                {provider?.businessType || "알 수 없음"}
              </td>
              <td className="bg-[#fafcff] whitespace-nowrap px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 font-semibold text-gray-600 border-r border-[#eef2f7]">
                담당자
              </td>
              <td className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 text-gray-900 text-2xs sm:text-xs md:text-sm">
                {provider?.managerName || "알 수 없음"} (
                {provider?.managerPhone
                  ? formatPhoneNumber(provider.managerPhone)
                  : "알 수 없음"}
                )
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </MaskedSection>
  );
}
